import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { Input } from '@/components/form/Input';
import { ProfilePictureUpload } from '@/components/ProfilePictureUpload';
import { Tooltip } from '@/components/Tooltip';
import { StepProps } from '@/sections/customerManagement/components/modals/AddCustomerModal/AddCustomerModal.types';
import { CustomerTypeChangeConfirmationDialog } from '@/sections/customerManagement/components/modals/AddCustomerModal/components';
import { CustomerTypeOptions } from '@/sections/customerManagement/components/modals/UpdateCustomerTypeModal';
import {
  DEFAULT_FORM_DATA,
  getDefaultsForType,
  MANAGED_SUBSCRIPTION_CONFIG,
  SAAS_SUBSCRIPTION_CONFIG,
} from '@/sections/customerManagement/constants';
import { CustomerTypeValue } from '@/types';

/**
 * Step 1: Basic Information - Organization name, profile picture, and subscription type
 */
export const BasicInformationStep: React.FC<StepProps> = ({
  formData,
  setFormData,
  isSubmitting,
}) => {
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    newType: CustomerTypeValue | null;
  }>({
    isOpen: false,
    newType: null,
  });

  const hasUserSetValues = (): boolean => {
    // Get type-aware defaults for CURRENT customer type
    // This accounts for auto-initialized values (e.g., subscription_assets = 0 for MANAGED)
    const typeDefaults = getDefaultsForType(formData.customer_type);

    // Compare current formData against type-aware defaults
    // Only returns true if user has manually modified values beyond auto-initialization
    return (
      // Attack surface selections (Step 2)
      JSON.stringify(formData.attack_surface) !==
        JSON.stringify(typeDefaults.attack_surface) ||
      // Period of performance dates (Step 3)
      formData.subscription_start_date !==
        typeDefaults.subscription_start_date ||
      formData.subscription_end_date !== typeDefaults.subscription_end_date ||
      // Subscription pricing (Step 4) - compared against type-aware defaults
      formData.subscription_cost !== typeDefaults.subscription_cost ||
      formData.subscription_msrp !== typeDefaults.subscription_msrp ||
      formData.subscription_assets !== typeDefaults.subscription_assets ||
      formData.subscription_applications !==
        typeDefaults.subscription_applications ||
      formData.subscription_tier !== typeDefaults.subscription_tier ||
      formData.subscription_scan_schedule !==
        typeDefaults.subscription_scan_schedule ||
      // PlexTrac configuration (Step 3)
      formData.plextrac_id !== typeDefaults.plextrac_id ||
      // Team members (Steps 5-6)
      formData.collaborators.length !== typeDefaults.collaborators.length
    );
  };

  const handleCustomerTypeChange = (newType: CustomerTypeValue | null) => {
    // If null is selected, ignore (shouldn't happen in practice)
    if (!newType) return;

    // If this is the initial selection (no current type), apply directly
    if (!formData.customer_type) {
      applyCustomerTypeChange(newType);
      return;
    }

    // If changing type and user has set values, show confirmation dialog
    if (formData.customer_type !== newType) {
      if (hasUserSetValues()) {
        setConfirmationDialog({
          isOpen: true,
          newType,
        });
      } else {
        // No user-set values, apply change directly
        applyCustomerTypeChange(newType);
      }
    }
  };

  const applyCustomerTypeChange = (newType: CustomerTypeValue) => {
    // Reset to default form data but preserve basic info
    const resetFormData = {
      ...DEFAULT_FORM_DATA,
      // Preserve basic information
      settings_display_name: formData.settings_display_name,
      profile_picture: formData.profile_picture,
      // Set new customer type
      customer_type: newType,
    };

    // Apply customer type-specific defaults
    if (newType === 'FREEMIUM') {
      resetFormData.attack_surface = DEFAULT_FORM_DATA.attack_surface;
    }

    // Initialize subscription fields for MANAGED/SAAS customer types
    if (newType === 'MANAGED' || newType === 'SAAS') {
      const config =
        newType === 'MANAGED'
          ? MANAGED_SUBSCRIPTION_CONFIG
          : SAAS_SUBSCRIPTION_CONFIG;
      resetFormData.subscription_tier = config.selectedTier;
      resetFormData.subscription_scan_schedule = config.scanSchedule;
      resetFormData.subscription_assets = 0;
      resetFormData.subscription_applications = 0;
    }

    setFormData(resetFormData);
  };

  const handleConfirmTypeChange = () => {
    if (confirmationDialog.newType) {
      applyCustomerTypeChange(confirmationDialog.newType);
    }
    setConfirmationDialog({ isOpen: false, newType: null });
  };

  const handleCancelTypeChange = () => {
    setConfirmationDialog({ isOpen: false, newType: null });
  };
  return (
    <div className="flex h-[700px] flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="mb-8">
          <div className="flex gap-6">
            <div className="flex flex-col gap-2">
              <div className="mb-2">
                <div className="flex items-center gap-2">
                  <label className="text-chariot-text-primary block text-sm font-medium">
                    Customer Logo{' '}
                    <span className="text-chariot-stroke-critical">*</span>
                  </label>
                  <Tooltip title="Upload your customer's logo (required)">
                    <QuestionMarkCircleIcon className="text-chariot-text-secondary hover:text-chariot-text-primary h-4 w-4 cursor-help" />
                  </Tooltip>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <ProfilePictureUpload
                  source={
                    formData.profile_picture
                      ? { type: 'buffer', buffer: formData.profile_picture }
                      : { type: 'empty' }
                  }
                  onUpload={buffer =>
                    setFormData(prev => ({ ...prev, profile_picture: buffer }))
                  }
                  onDelete={() =>
                    setFormData(prev => ({
                      ...prev,
                      profile_picture: undefined,
                    }))
                  }
                  size="sm"
                  shape="square"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Vertical divider */}
            <div className="border-chariot-stroke w-px self-stretch border-l" />

            <div className="flex flex-1 flex-col">
              <div className="relative">
                <Input
                  label={
                    <>
                      Customer Name{' '}
                      <span className="text-chariot-stroke-critical">*</span>
                    </>
                  }
                  name="settings_display_name"
                  value={formData.settings_display_name}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      settings_display_name: e.target.value,
                    }))
                  }
                  placeholder="Enter customer name"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <div className="mb-6 flex items-center gap-2">
            <h3 className="text-chariot-text-primary text-base font-medium">
              Select Subscription Type{' '}
              <span className="text-chariot-stroke-critical">*</span>
            </h3>
            <Tooltip title="Choose the type of subscription that best fits this customer's needs">
              <QuestionMarkCircleIcon className="text-chariot-text-secondary hover:text-chariot-text-primary h-4 w-4 cursor-help" />
            </Tooltip>
          </div>
          <div className="space-y-3">
            <CustomerTypeOptions
              selectedType={formData.customer_type}
              setSelectedType={handleCustomerTypeChange}
              isPending={isSubmitting}
            />
          </div>
        </div>
      </div>

      <CustomerTypeChangeConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onConfirm={handleConfirmTypeChange}
        onCancel={handleCancelTypeChange}
        currentType={formData.customer_type}
        newType={confirmationDialog.newType!}
      />
    </div>
  );
};
